import React, { useState, useEffect } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Search from '../../components/Search/Search';
import ClientList from '../../components/ClientList/ClientList';
import ClientDetailsCard from '../../components/ClientDetailsCard/ClientDetailsCard';
import Button from '../../components/ui/Button';

const Clients = () => {
    const [isCardOpen, setIsCardOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({
        field: '',
        direction: 'asc'
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [allClients, setAllClients] = useState([]);

    const navigate = useNavigate(); 

    useEffect(() => {
        localStorage.removeItem('clientsCurrentPage');
    }, []);

    const handleClientClick = (client) => {
        setSelectedClient(client);
        setIsCardOpen(true);
    };

    const closeCard = () => {
        setIsCardOpen(false);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
    };

    const handleStatusChange = (status) => {
        setSelectedStatus(status); 
    };

    const handleSortChange = (field, direction) => {
        setSortConfig({
            field,
            direction
        });
    };

    const handleNewClientClick = () => {
        navigate('/new-client-form');
    };

    const handleImportClientsClick = () => {
        navigate('/import-client');
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCheckboxChange = (clientId, isChecked) => {
        setSelectedClients(prev => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(clientId);
            } else {
                newSet.delete(clientId);
            }
            return newSet;
        });
    };
    
    const handleSelectAll = (clientsArray) => {
        if (clientsArray.length > 0) {
            setSelectedClients(new Set(clientsArray.map(client => client.id)));
        } else {
            setSelectedClients(new Set());
        }
    };
    
    const handleBulkUpdateClick = () => {
        navigate('/bulk-update-clients', { state: { selectedClientsData: getSelectedClientsData() } });
    };

    const handleClientsLoad = (clientsData) => {
        setAllClients(clientsData);
    };

    const getSelectedClientsData = () => {
        return allClients.filter(client => selectedClients.has(client.id));
    };

    return (
        <div className="relative mx-auto flex min-h-[80vh] max-w-layout flex-col rounded-lg bg-white p-5 shadow-card sm:p-7">
            <div className="flex-1">
                <h1 className="mb-4">All Clients</h1>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="w-full sm:flex-1">
                        <Search
                            onStatusChange={handleStatusChange}
                            onSearch={handleSearch}
                            onSortChange={handleSortChange}
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        {selectedClients.size > 0 && (
                            <Button onClick={handleBulkUpdateClick}>
                                Update {selectedClients.size} Selected Client{selectedClients.size > 1 ? 's' : ''}
                            </Button>
                        )}
                        <Button
                            onClick={handleMenuClick}
                            aria-label="Add client"
                            className="!px-4 text-2xl leading-none"
                        >
                            +
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={() => {
                                handleMenuClose();
                                handleNewClientClick();
                            }}>
                                New Client
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleMenuClose();
                                handleImportClientsClick();
                            }}>
                                Import Clients
                            </MenuItem>
                        </Menu>
                    </div>
                </div>

                <div className="mt-5">
                    <ClientList
                        onClientClick={handleClientClick}
                        selectedStatus={selectedStatus}
                        searchQuery={searchQuery}
                        sortConfig={sortConfig}
                        selectedClients={selectedClients}
                        onCheckboxChange={handleCheckboxChange}
                        onSelectAll={handleSelectAll}
                        onClientsLoad={handleClientsLoad}
                    />
                </div>
            </div>

            {isCardOpen && (
                <ClientDetailsCard
                    client={selectedClient}
                    isOpen={isCardOpen}
                    onClose={closeCard}
                />
            )}
        </div>
    );
};

export default Clients;